import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MonitorDown, Share, SquarePlus, MoreVertical, RefreshCw, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { pwa, isStandalone, isIOS } from '@/lib/pwa';

// CTH V6.0 — discreet "Install CTH" entry. Hidden when already running as an
// installed app; triggers the native prompt where supported (Android/desktop
// Chrome/Edge) and shows step-by-step guidance otherwise (iOS Safari, etc.).
export const InstallCTH = ({ variant = 'ghost', className = '', iconOnly = false }) => {
  const { i18n } = useTranslation();
  const es = (i18n.language || 'es').startsWith('es');
  const L = (a, b) => (es ? a : b);
  const [canPrompt, setCanPrompt] = useState(pwa.canPrompt());
  const [guideOpen, setGuideOpen] = useState(false);
  const [standalone] = useState(isStandalone());

  useEffect(() => pwa.subscribe(() => setCanPrompt(pwa.canPrompt())), []);
  if (standalone) return null;

  const onClick = async () => {
    if (canPrompt) {
      const outcome = await pwa.prompt();
      if (outcome === 'accepted') toast.success(L('CTH instalado correctamente.', 'CTH installed successfully.'));
      return;
    }
    setGuideOpen(true);
  };

  const ios = isIOS();
  return (
    <>
      <Button variant={variant} size="sm" onClick={onClick} className={`gap-1.5 ${className}`}
              title={L('Instalar CTH', 'Install CTH')} data-testid="install-cth-btn">
        <MonitorDown className="h-4 w-4" />
        {!iconOnly && <span className="hidden lg:inline">{L('Instalar CTH', 'Install CTH')}</span>}
      </Button>
      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] rounded-lg" data-testid="install-cth-guide">
          <DialogHeader>
            <DialogTitle>{L('Instalar Corporate Talent Hub', 'Install Corporate Talent Hub')}</DialogTitle>
            <DialogDescription>
              {L('Agrega CTH a tu pantalla de inicio para acceder como una aplicación.',
                 'Add CTH to your home screen to use it like an app.')}
            </DialogDescription>
          </DialogHeader>
          {ios ? (
            <ol className="space-y-3 text-sm" data-testid="install-guide-ios">
              <li className="flex items-start gap-2.5">
                <Share className="h-4 w-4 mt-0.5 text-teal-700 shrink-0" />
                <span>{L('1. En Safari, toca el botón Compartir (cuadro con flecha hacia arriba).',
                         '1. In Safari, tap the Share button (square with an upward arrow).')}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <SquarePlus className="h-4 w-4 mt-0.5 text-teal-700 shrink-0" />
                <span>{L('2. Desliza y selecciona "Agregar a pantalla de inicio".',
                         '2. Scroll and select "Add to Home Screen".')}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-teal-700 shrink-0" />
                <span>{L('3. Confirma el nombre "CTH" y toca "Agregar". El icono aparecerá en tu pantalla de inicio.',
                         '3. Confirm the name "CTH" and tap "Add". The icon will appear on your home screen.')}</span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm" data-testid="install-guide-generic">
              <li className="flex items-start gap-2.5">
                <MoreVertical className="h-4 w-4 mt-0.5 text-teal-700 shrink-0" />
                <span>{L('1. Abre el menú del navegador (⋮ en Chrome/Edge).',
                         '1. Open the browser menu (⋮ in Chrome/Edge).')}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <MonitorDown className="h-4 w-4 mt-0.5 text-teal-700 shrink-0" />
                <span>{L('2. Selecciona "Instalar aplicación" o "Agregar a pantalla de inicio".',
                         '2. Select "Install app" or "Add to Home screen".')}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-teal-700 shrink-0" />
                <span>{L('3. Confirma para instalar Corporate Talent Hub.',
                         '3. Confirm to install Corporate Talent Hub.')}</span>
              </li>
            </ol>
          )}
          <p className="text-xs text-muted-foreground">
            {L('CTH seguirá disponible desde el navegador con normalidad si prefieres no instalarla.',
               'CTH remains fully available from the browser if you prefer not to install it.')}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Listens for a newly deployed version and offers a one-tap refresh.
export const PwaUpdateNotice = () => {
  const { i18n } = useTranslation();
  useEffect(() => {
    const onUpdate = (e) => {
      const reg = e.detail;
      const es = (i18n.language || 'es').startsWith('es');
      toast.info(es ? 'Nueva versión de CTH disponible' : 'A new CTH version is available', {
        id: 'cth-sw-update',
        duration: 30000,
        icon: <RefreshCw className="h-4 w-4" />,
        action: {
          label: es ? 'Actualizar' : 'Update',
          onClick: () => {
            try {
              if (reg?.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
              else window.location.reload();
            } catch { window.location.reload(); }
          },
        },
      });
    };
    window.addEventListener('cth:sw-update', onUpdate);
    return () => window.removeEventListener('cth:sw-update', onUpdate);
  }, [i18n.language]);
  return null;
};
